import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CaretRight } from "@phosphor-icons/react";
import useDocumentMeta from "../lib/useDocumentMeta";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SEOIndex() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/seo/manifest`)
      .then((r) => r.json())
      .then((d) => setPages(d.pages || []))
      .catch(() => setPages([]))
      .finally(() => setLoading(false));
  }, []);

  useDocumentMeta({
    title: "Estima Long-form 2026 India Property Guides — Rent vs Buy, EMI, Snowball, Section 24B",
    description:
      "Long-form, INR-first 2026 guides on rent vs buy, ₹50 lakh / ₹1 crore home loan EMI, Section 24B, Rental Snowball, CIBIL 725 eligibility, stamp duty, and property XIRR — all with worked Indian examples.",
    keywords:
      "rent vs buy india 2026, ₹50 lakh home loan emi, ₹1 crore home loan eligibility, section 24b 2026, rental snowball india, cibil 725 home loan, stamp duty mumbai bangalore 2026, property xirr india",
    canonical: "https://estima.co.in/seo",
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <nav className="border-b hairline">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="font-serif text-xl"
            data-testid="seo-index-home-link"
          >
            Estima
          </Link>
          <Link
            to="/register"
            className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2"
            data-testid="seo-index-cta"
          >
            Get started <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </nav>

      <header className="max-w-3xl mx-auto px-6 pt-16 pb-8">
        <div className="eyebrow text-[hsl(var(--secondary))] mb-3">
          Estima · 2026 India guides
        </div>
        <h1 className="font-serif text-4xl md:text-5xl leading-tight">
          Long-form property guides — the questions India is actually asking in
          2026.
        </h1>
        <p className="text-muted-foreground mt-4 text-lg leading-relaxed">
          Each guide answers a single high-intent question with real Indian
          numbers — Section 24B caps, current RBI repo rate, CIBIL band rate
          premiums, women-buyer stamp duty discounts, and city-specific
          micro-market data. Updated for Budget 2026.
        </p>
      </header>

      <main
        className="max-w-3xl mx-auto px-6 pb-24"
        data-testid="seo-index-pages-list"
      >
        {loading && (
          <div className="text-muted-foreground py-12 text-center">
            Loading guides…
          </div>
        )}
        {!loading && pages.length === 0 && (
          <div className="text-muted-foreground py-12 text-center">
            No guides published yet.
          </div>
        )}
        <ul className="divide-y hairline border-y hairline">
          {pages.map((p) => (
            <li key={p.slug}>
              <Link
                to={`/seo/${p.slug}`}
                className="flex items-start justify-between gap-6 py-6 group hover:bg-[hsl(var(--muted))]/30 px-2 -mx-2 rounded transition-colors"
                data-testid={`seo-index-link-${p.slug}`}
              >
                <div>
                  <h2 className="font-serif text-xl leading-snug group-hover:text-[hsl(var(--primary))]">
                    {p.h1 || p.title}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    {p.description}
                  </p>
                </div>
                <CaretRight
                  size={18}
                  weight="bold"
                  className="text-muted-foreground shrink-0 mt-1 group-hover:text-[hsl(var(--primary))] transition-colors"
                />
              </Link>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}
